import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ReactFlow, Background, Controls, MiniMap, useNodesState, useEdgesState, addEdge, Handle, Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Play, Zap, MessageSquare, Tag, Clock, GitBranch, StopCircle, Plus, HelpCircle, Trash2, FileText, UserPlus, Hash, Loader2,
} from "lucide-react";
import { groupTagsByCategory } from "@/lib/tagUtils";

const NODE_DEFS = [
  // Triggers
  { type: "trigger_keyword", group: "trigger", label: "Keyword", icon: Hash, color: "border-t-amber-400" },
  { type: "trigger_new_contact", group: "trigger", label: "New Contact", icon: UserPlus, color: "border-t-amber-400" },
  { type: "trigger_tag_added", group: "trigger", label: "Tag Added", icon: Tag, color: "border-t-amber-400" },
  { type: "trigger_broadcast_reply", group: "trigger", label: "Broadcast Reply", icon: MessageSquare, color: "border-t-amber-400" },
  // Actions
  { type: "action_send_message", group: "action", label: "Send Message", icon: MessageSquare, color: "border-t-brand" },
  { type: "action_send_template", group: "action", label: "Send Template", icon: FileText, color: "border-t-brand" },
  { type: "action_ask_question", group: "action", label: "Ask Question", icon: HelpCircle, color: "border-t-violet-500" },
  { type: "action_add_tag", group: "action", label: "Add Tag", icon: Tag, color: "border-t-emerald-500" },
  { type: "action_remove_tag", group: "action", label: "Remove Tag", icon: Tag, color: "border-t-rose-500" },
  { type: "action_delay", group: "action", label: "Delay", icon: Clock, color: "border-t-slate-400" },
  { type: "action_condition", group: "action", label: "Condition", icon: GitBranch, color: "border-t-orange-500" },
  { type: "action_end", group: "action", label: "End", icon: StopCircle, color: "border-t-slate-700" },
];

const findDef = (type) => NODE_DEFS.find((d) => d.type === type) || NODE_DEFS[0];

function CustomNode({ data, selected }) {
  const def = findDef(data.nodeType);
  const Icon = def.icon;
  return (
    <div className={`bg-white border-t-4 ${def.color} border border-slate-200 rounded-lg shadow-sm w-60 overflow-hidden ${selected ? "ring-2 ring-brand" : ""}`}>
      <Handle type="target" position={Position.Top} className="!bg-slate-300" />
      <div className="px-3 py-2 flex items-center gap-2 border-b border-slate-100">
        <Icon className="w-3.5 h-3.5 text-slate-500" />
        <span className="text-xs font-semibold tracking-wider uppercase text-slate-500">{def.group}</span>
      </div>
      <div className="px-3 py-2">
        <div className="font-medium text-sm text-slate-900">{def.label}</div>
        {data.nodeType === "action_send_message" && data.message && (
          <div className="text-xs text-slate-500 mt-1 line-clamp-2">{data.message}</div>
        )}
        {data.nodeType === "trigger_keyword" && data.keyword && (
          <div className="text-xs text-slate-500 mt-1">Keyword: <code className="bg-slate-100 px-1 rounded">{data.keyword}</code></div>
        )}
        {data.nodeType === "action_delay" && (
          <div className="text-xs text-slate-500 mt-1">{data.minutes || 0}m {data.seconds || 0}s</div>
        )}
      </div>
      {data.nodeType === "action_condition" ? (
        <>
          <Handle type="source" position={Position.Bottom} id="yes" style={{ left: "30%" }} className="!bg-emerald-500" />
          <Handle type="source" position={Position.Bottom} id="no" style={{ left: "70%" }} className="!bg-rose-500" />
        </>
      ) : (
        <Handle type="source" position={Position.Bottom} className="!bg-slate-300" />
      )}
    </div>
  );
}

export default function FlowBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [flow, setFlow] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [tags, setTags] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [saving, setSaving] = useState(false);

  const nodeTypes = useMemo(() => ({ custom: CustomNode }), []);

  const reshapeFromBackend = (f) => {
    const ns = (f.nodes || []).map((n) => ({
      id: n.id,
      type: "custom",
      position: n.position || { x: 0, y: 0 },
      data: { nodeType: n.type, ...(n.data || {}) },
    }));
    const es = (f.edges || []).map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.source_handle || undefined,
      label: e.label,
    }));
    return { ns, es };
  };

  useEffect(() => {
    api.get(`/flows/${id}`).then(({ data }) => {
      setFlow(data.flow);
      const { ns, es } = reshapeFromBackend(data.flow);
      setNodes(ns); setEdges(es);
    });
    api.get("/tags").then(({ data }) => setTags(data));
    api.get("/contacts?limit=20").then(({ data }) => setContacts(data.items));
    api.get("/custom-fields").then(({ data }) => setCustomFields(data));
    // eslint-disable-next-line
  }, [id]);

  const onConnect = useCallback((p) => setEdges((eds) => addEdge({ ...p, id: `e_${Math.random().toString(36).slice(2, 9)}` }, eds)), [setEdges]);

  const addNode = (type) => {
    const def = findDef(type);
    const newNode = {
      id: `n_${Math.random().toString(36).slice(2, 9)}`,
      type: "custom",
      position: { x: 250 + Math.random() * 100, y: 150 + Math.random() * 100 },
      data: { nodeType: type, label: def.label },
    };
    setNodes((nds) => [...nds, newNode]);
    setSelectedNodeId(newNode.id);
  };

  const updateNodeData = (key, value) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === selectedNodeId ? { ...n, data: { ...n.data, [key]: value } } : n))
    );
  };

  const deleteSelected = () => {
    if (!selectedNodeId) return;
    setNodes((nds) => nds.filter((n) => n.id !== selectedNodeId));
    setEdges((eds) => eds.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId));
    setSelectedNodeId(null);
  };

  const save = async (activate = null) => {
    setSaving(true);
    try {
      const backendNodes = nodes.map((n) => {
        const { nodeType, label, ...rest } = n.data;
        return { id: n.id, type: nodeType, position: n.position, data: rest };
      });
      const backendEdges = edges.map((e) => ({
        id: e.id, source: e.source, target: e.target, source_handle: e.sourceHandle || null,
      }));
      const payload = { nodes: backendNodes, edges: backendEdges };
      if (activate !== null) payload.active = activate;
      await api.patch(`/flows/${id}`, payload);
      const { data } = await api.get(`/flows/${id}`);
      setFlow(data.flow);
      toast.success(activate === null ? "Saved" : (activate ? "Activated" : "Deactivated"));
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const testFlow = async () => {
    if (contacts.length === 0) return toast.error("Add a contact first");
    const contactId = contacts[0].id;
    await save();
    try {
      await api.post(`/flows/${id}/test`, { contact_id: contactId });
      toast.success(`Test run started for ${contacts[0].name}`);
    } catch (e) {
      toast.error("Test failed");
    }
  };

  const selectedNode = nodes.find((n) => n.id === selectedNodeId);

  if (!flow) return <div className="p-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="flex flex-col h-screen bg-slate-50" data-testid="flow-builder-page">
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/flows" className="text-slate-500 hover:text-slate-900"><ArrowLeft className="w-4 h-4" /></Link>
          <div>
            <div className="font-heading font-semibold text-slate-900">{flow.name}</div>
            <div className="text-xs text-slate-500">{nodes.length} nodes · {edges.length} edges</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 mr-3">
            <Switch
              checked={!!flow.active}
              onCheckedChange={(v) => save(v)}
              data-testid="flow-active-switch"
            />
            <span className="text-xs text-slate-600">{flow.active ? "Active" : "Inactive"}</span>
          </div>
          <Button variant="outline" onClick={testFlow} data-testid="flow-test-button">
            <Play className="w-4 h-4 mr-1.5" />Test
          </Button>
          <Button onClick={() => save()} disabled={saving} className="bg-brand hover:bg-brand-dark" data-testid="flow-save-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" />Save</>}
          </Button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Node palette */}
        <div className="w-56 shrink-0 border-r border-slate-200 bg-white p-3 overflow-y-auto">
          <div className="label-caps mb-2">Triggers</div>
          <div className="space-y-1">
            {NODE_DEFS.filter((d) => d.group === "trigger").map((d) => {
              const Icon = d.icon;
              return (
                <button
                  key={d.type}
                  onClick={() => addNode(d.type)}
                  data-testid={`add-node-${d.type}`}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-amber-50 text-slate-700"
                >
                  <Icon className="w-3.5 h-3.5 text-amber-600" /> {d.label}
                </button>
              );
            })}
          </div>
          <div className="label-caps mt-4 mb-2">Actions</div>
          <div className="space-y-1">
            {NODE_DEFS.filter((d) => d.group === "action").map((d) => {
              const Icon = d.icon;
              return (
                <button
                  key={d.type}
                  onClick={() => addNode(d.type)}
                  data-testid={`add-node-${d.type}`}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-slate-100 text-slate-700"
                >
                  <Icon className="w-3.5 h-3.5 text-slate-500" /> {d.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 relative">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            onNodeClick={(_, node) => setSelectedNodeId(node.id)}
            onPaneClick={() => setSelectedNodeId(null)}
            fitView
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={16} color="#CBD5E1" />
            <Controls />
            <MiniMap nodeColor="#94A3B8" maskColor="rgba(241,245,249,0.6)" />
          </ReactFlow>
        </div>

        {/* Right inspector */}
        <div className="w-80 shrink-0 border-l border-slate-200 bg-white p-5 overflow-y-auto">
          {!selectedNode ? (
            <div className="text-sm text-slate-400">
              Click a node to edit its properties, or use the left palette to add new nodes.
            </div>
          ) : (
            <div data-testid="node-inspector">
              <div className="flex items-center justify-between">
                <div className="label-caps">{findDef(selectedNode.data.nodeType).label}</div>
                <button onClick={deleteSelected} className="text-slate-400 hover:text-red-600" data-testid="delete-node-button">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="mt-4 space-y-3">
                {selectedNode.data.nodeType === "trigger_keyword" && (
                  <>
                    <div>
                      <Label className="text-xs">Keyword</Label>
                      <Input value={selectedNode.data.keyword || ""} onChange={(e) => updateNodeData("keyword", e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Match type</Label>
                      <Select value={selectedNode.data.match || "contains"} onValueChange={(v) => updateNodeData("match", v)}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="contains">Contains</SelectItem>
                          <SelectItem value="exact">Exact match</SelectItem>
                          <SelectItem value="starts_with">Starts with</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                {selectedNode.data.nodeType === "trigger_tag_added" && (
                  <div>
                    <Label className="text-xs">Tag (leave blank for any)</Label>
                    <Select value={selectedNode.data.tag_id || ""} onValueChange={(v) => updateNodeData("tag_id", v)}>
                      <SelectTrigger className="mt-1" data-testid="trigger-tag-select">
                        <SelectValue placeholder="Any tag" />
                      </SelectTrigger>
                      <SelectContent>
                        {tags.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">Fires when this tag is assigned to any contact.</p>
                  </div>
                )}

                {selectedNode.data.nodeType === "action_send_message" && (
                  <div>
                    <Label className="text-xs">Message</Label>
                    <textarea
                      value={selectedNode.data.message || ""}
                      onChange={(e) => updateNodeData("message", e.target.value)}
                      rows={5}
                      className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                      data-testid="node-message-input"
                    />
                  </div>
                )}

                {selectedNode.data.nodeType === "action_send_template" && (
                  <div>
                    <Label className="text-xs">Template name</Label>
                    <Input value={selectedNode.data.template_name || ""} onChange={(e) => updateNodeData("template_name", e.target.value)} className="mt-1" />
                  </div>
                )}

                {selectedNode.data.nodeType === "action_ask_question" && (
                  <>
                    <div>
                      <Label className="text-xs">Question</Label>
                      <Input value={selectedNode.data.question || ""} onChange={(e) => updateNodeData("question", e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Save response to key</Label>
                      <Input value={selectedNode.data.save_key || ""} onChange={(e) => updateNodeData("save_key", e.target.value)} className="mt-1" placeholder="e.g. email" />
                    </div>
                  </>
                )}

                {(selectedNode.data.nodeType === "action_add_tag" || selectedNode.data.nodeType === "action_remove_tag") && (
                  <div>
                    <Label className="text-xs">Tag</Label>
                    <Select value={selectedNode.data.tag_id || ""} onValueChange={(v) => updateNodeData("tag_id", v)}>
                      <SelectTrigger className="mt-1" data-testid="action-tag-select"><SelectValue placeholder="Select tag" /></SelectTrigger>
                      <SelectContent>
                        {groupTagsByCategory(tags, { excludeInactive: true }).map(({ category, tags: catTags }) => (
                          <div key={category}>
                            <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{category}</div>
                            {catTags.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                          </div>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {selectedNode.data.nodeType === "action_delay" && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Minutes</Label>
                      <Input type="number" value={selectedNode.data.minutes || 0} onChange={(e) => updateNodeData("minutes", parseInt(e.target.value) || 0)} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Seconds</Label>
                      <Input type="number" value={selectedNode.data.seconds || 0} onChange={(e) => updateNodeData("seconds", parseInt(e.target.value) || 0)} className="mt-1" />
                    </div>
                  </div>
                )}

                {selectedNode.data.nodeType === "action_condition" && (
                  <>
                    <div>
                      <Label className="text-xs">Field</Label>
                      <Select value={selectedNode.data.field || ""} onValueChange={(v) => updateNodeData("field", v)}>
                        <SelectTrigger className="mt-1" data-testid="condition-field-select"><SelectValue placeholder="Select field" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="status">Status</SelectItem>
                          <SelectItem value="city">City</SelectItem>
                          <SelectItem value="country">Country</SelectItem>
                          <SelectItem value="lead_source">Lead source</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="name">Name</SelectItem>
                          {customFields.length > 0 && (
                            <>
                              <div className="px-2 pt-2 pb-1 text-[10px] font-semibold tracking-wider uppercase text-slate-400">
                                Custom fields
                              </div>
                              {customFields.map((cf) => (
                                <SelectItem
                                  key={cf.id}
                                  value={`custom_fields.${cf.key}`}
                                  data-testid={`condition-field-custom-${cf.key}`}
                                >
                                  {cf.name} <span className="text-slate-400">({cf.type})</span>
                                </SelectItem>
                              ))}
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Operator</Label>
                      <Select value={selectedNode.data.operator || "equals"} onValueChange={(v) => updateNodeData("operator", v)}>
                        <SelectTrigger className="mt-1" data-testid="condition-operator-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="equals">Equals</SelectItem>
                          <SelectItem value="not_equals">Not equals</SelectItem>
                          <SelectItem value="contains">Contains</SelectItem>
                          <SelectItem value="greater_than">Greater than</SelectItem>
                          <SelectItem value="less_than">Less than</SelectItem>
                          <SelectItem value="empty">Is empty</SelectItem>
                          <SelectItem value="not_empty">Is not empty</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {!["empty", "not_empty"].includes(selectedNode.data.operator || "equals") && (
                      <div>
                        <Label className="text-xs">Value</Label>
                        <Input
                          value={selectedNode.data.value || ""}
                          onChange={(e) => updateNodeData("value", e.target.value)}
                          className="mt-1"
                          data-testid="condition-value-input"
                        />
                      </div>
                    )}
                    <p className="text-xs text-slate-500">Connects to next nodes via Yes (left/green) and No (right/red) handles.</p>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
